import type { HTMLAttributes, ReactNode } from 'react';
import type { ChartPoint } from '../ColumnChart/chart-point.ts';
import type { ValueScale } from '../ColumnChart/chart-scale.ts';
import { BucketLabels } from './BucketLabels.tsx';
import { ChartLegend } from './ChartLegend.tsx';
import type { ChartSeries, SeriesKey } from './chart-series.ts';
import { SeriesTable } from './SeriesTable.tsx';
import { Gridlines, ValueTicks } from './ValueAxis.tsx';
import styles from './Chart.module.css';

export type PlotFrameProps = {
  label: string;
  scale: ValueScale;
  formatTick: (value: number) => string;
  buckets: readonly ChartPoint[];
  placeAt: (index: number, count: number) => number;
  plotProps: HTMLAttributes<HTMLDivElement>;
  children: ReactNode;
};

export const PlotFrame = ({
  label,
  scale,
  formatTick,
  buckets,
  placeAt,
  plotProps,
  children,
}: PlotFrameProps) => (
  <div className={styles['frame']}>
    <ValueTicks scale={scale} formatTick={formatTick} />
    <div className={styles['plot']} aria-label={label} role="group" {...plotProps}>
      <Gridlines scale={scale} />
      {children}
    </div>
    <BucketLabels buckets={buckets} placeAt={placeAt} />
  </div>
);

export type ChartFigureProps = {
  label: string;
  keys: readonly SeriesKey[];
  series: readonly ChartSeries[];
  children: ReactNode;
};

export const ChartFigure = ({ label, keys, series, children }: ChartFigureProps) => (
  <figure className={styles['figure']}>
    <figcaption className="visually-hidden">{label}</figcaption>
    {keys.length > 1 ? <ChartLegend keys={keys} /> : null}
    {children}
    <SeriesTable label={label} series={series} />
  </figure>
);
